import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import html2canvas from 'html2canvas';
import { useAuth } from '../contexts/AuthContext';
import './AthleteDetails.css';
import Header from './Header';
import MobileBottomNav from './MobileBottomNav';
import AthleteCard from './AthleteCard';

// Generate the spotlight URL for sharing
const getSpotlightUrl = (athleteId) => {
  const baseUrl = window.location.origin;
  // If we're on spotlight subdomain, don't add /spotlight prefix
  if (window.location.hostname.includes('spotlight.legacystat.com')) {
    return `${baseUrl}/${athleteId}`;
  }
  return `${baseUrl}/spotlight/${athleteId}`;
};

// Extract YouTube video ID from various YouTube URL formats
const getYouTubeVideoId = (url) => {
  if (!url) return null;

  // Handle different YouTube URL formats
  const patterns = [
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=([^&]+)/,
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/embed\/([^?]+)/,
    /(?:https?:\/\/)?youtu\.be\/([^?]+)/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }

  return null;
};

// Check if URL is a YouTube URL
const isYouTubeUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  return url.includes('youtube.com') || url.includes('youtu.be');
};

// Check if URL is a Cloudflare Stream URL
const isCloudflareStreamUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  return url.includes('cloudflarestream.com');
};

// SVG Path data for athlete card

const AthleteDetails = ({ setActiveScreen, athleteId }) => {
  const { getSpotlightById, trackAnalytics, loading } = useAuth();
  const location = useLocation();
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [downloadFormat, setDownloadFormat] = useState('poster'); // 'poster', 'vertical', 'square'
  const [athlete, setAthlete] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const downloadCardRef = useRef(null);

  useEffect(() => {
    if (athleteId) {
      fetchAthlete();
    }
  }, [athleteId]);

  // Auto-flip icon every 5 seconds continuously
  useEffect(() => {
    const interval = setInterval(() => {
      setIsDescriptionExpanded(prev => !prev);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  // Apply word-boundary truncation to download modal text after render
  useEffect(() => {
    if (!isDownloadModalOpen || !downloadCardRef.current) return;

    // Wait for the card to fully render and layout
    const timer = setTimeout(() => {
      if (!downloadCardRef.current) return;
      const textElements = downloadCardRef.current.querySelectorAll('.athlete-details-download-legacy-text, .square-spotlight-text');

      textElements.forEach((el) => {
        const text = el.textContent;
        const words = text.split(' ');
        if (words.length <= 1) return;

        // Save original styles and temporarily remove line-clamp so we can measure natural height
        const origDisplay = el.style.display;
        const origClamp = el.style.webkitLineClamp;
        const origOrient = el.style.webkitBoxOrient;
        const origOverflow = el.style.overflow;

        el.style.display = 'block';
        el.style.webkitLineClamp = 'unset';
        el.style.webkitBoxOrient = 'initial';
        el.style.overflow = 'visible';

        // Calculate the max height for exactly 2 lines
        const computedStyle = window.getComputedStyle(el);
        const lineHeight = parseFloat(computedStyle.lineHeight) || (parseFloat(computedStyle.fontSize) * 1.3);
        const maxHeight = Math.ceil(lineHeight * 2) + 1; // 2 lines with 1px tolerance

        // Check if text actually overflows 2 lines
        if (el.scrollHeight > maxHeight) {
          // Binary search by word count to find max words that fit in 2 lines
          let low = 0;
          let high = words.length;

          while (low < high) {
            const mid = Math.floor((low + high + 1) / 2);
            el.textContent = words.slice(0, mid).join(' ') + '...';
            if (el.scrollHeight <= maxHeight) {
              low = mid;
            } else {
              high = mid - 1;
            }
          }

          // Clean trailing punctuation before adding ellipsis
          let truncated = words.slice(0, low).join(' ').replace(/[\s,;:]+$/, '');
          el.textContent = truncated + '...';
        }

        // Restore original styles
        el.style.display = origDisplay;
        el.style.webkitLineClamp = origClamp;
        el.style.webkitBoxOrient = origOrient;
        el.style.overflow = origOverflow;
      });
    }, 100);

    return () => clearTimeout(timer);
  }, [isDownloadModalOpen, downloadFormat, athlete]);

  const fetchAthlete = async () => {
    const result = await getSpotlightById(athleteId);
    if (result.success) {
      setAthlete(result.data);
      // Track view
      trackAnalytics(athleteId, 'view');
    }
  };

  // Analytics tracking functions
  const handleShare = async () => {
    if (athlete?.id) {
      await trackAnalytics(athlete.id, 'share');
    }
    setIsShareModalOpen(true);
  };

  const handleCopyLink = async () => {
    const url = athlete?.id ? getSpotlightUrl(athlete.id) : window.location.href;

    // Try modern clipboard API first, fallback to older method
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = url;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      if (athlete?.id) {
        await trackAnalytics(athlete.id, 'copy_link');
      }
      alert('Link copied to clipboard!');
    } catch (err) {
      console.error('Copy failed:', err);
      alert('Failed to copy link. Please copy manually: ' + url);
    }
  };

  const handleInstagramShare = async () => {
    const url = athlete?.id ? getSpotlightUrl(athlete.id) : window.location.href;

    // Copy link to clipboard for Instagram (Instagram doesn't support direct web sharing)
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = url;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      if (athlete?.id) {
        await trackAnalytics(athlete.id, 'share');
      }
      alert('Link copied! Paste it in your Instagram post or story.');
    } catch (err) {
      console.error('Copy failed:', err);
      alert('Failed to copy link. Please copy manually: ' + url);
    }
  };

  const handleTikTokShare = async () => {
    const url = athlete?.id ? getSpotlightUrl(athlete.id) : window.location.href;

    // Copy link to clipboard for TikTok (TikTok doesn't support direct web sharing)
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = url;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      if (athlete?.id) {
        await trackAnalytics(athlete.id, 'share');
      }
      alert('Link copied! Paste it in your TikTok caption or bio.');
    } catch (err) {
      console.error('Copy failed:', err);
      alert('Failed to copy link. Please copy manually: ' + url);
    }
  };

  const handleMessagesShare = async () => {
    const url = athlete?.id ? getSpotlightUrl(athlete.id) : window.location.href;
    const text = `Check out this athlete spotlight: ${athlete?.athlete_name || 'Amazing Athlete'}`;

    // Try to use native share if available (works on mobile)
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'LegacyStat Spotlight',
          text: text,
          url: url
        });
        if (athlete?.id) {
          await trackAnalytics(athlete.id, 'share');
        }
      } catch (err) {
        // User cancelled or share failed
        console.log('Share cancelled');
      }
    } else {
      // Fallback: Open SMS with pre-filled message (works on most devices)
      const smsUrl = `sms:?body=${encodeURIComponent(text + ' ' + url)}`;
      window.location.href = smsUrl;
      if (athlete?.id) {
        await trackAnalytics(athlete.id, 'share');
      }
    }
  };

  const handleCopyCaption = async () => {
    if (athlete?.official_caption) {
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(athlete.official_caption);
        } else {
          // Fallback for older browsers
          const textArea = document.createElement('textarea');
          textArea.value = athlete.official_caption;
          textArea.style.position = 'fixed';
          textArea.style.left = '-999999px';
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
        }

        if (athlete?.id) {
          await trackAnalytics(athlete.id, 'copy_caption');
        }
        alert('Caption copied to clipboard!');
      } catch (err) {
        console.error('Copy failed:', err);
        alert('Failed to copy caption. Please copy manually: ' + athlete.official_caption);
      }
    }
  };

  // Detect iOS device
  const isIOS = () => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  };

  // Direct share for iOS - generates image and opens Share Sheet immediately
  const handleDirectShare = async (format) => {
    if (!downloadCardRef.current) return;

    setIsDownloading(true);
    let directShareRestoreImages = null;
    try {
      // Pre-crop all object-fit:cover images to match displayed crop
      directShareRestoreImages = await preCropAllImages(downloadCardRef.current);

      const canvas = await html2canvas(downloadCardRef.current, {
        backgroundColor: '#000000',
        scale: 4,
        useCORS: true,
        allowTaint: true,
        logging: false
      });

      // Recompose onto print-ready 3600×4500 canvas for poster (same as desktop)
      let finalCanvas = canvas;
      if (format === 'poster') {
        const TARGET_W = 3600;
        const TARGET_H = 4500;
        const PADDING = 120;
        const outputCanvas = document.createElement('canvas');
        outputCanvas.width = TARGET_W;
        outputCanvas.height = TARGET_H;
        const ctx = outputCanvas.getContext('2d');
        ctx.fillStyle = '#000000';
        ctx.fillRect(0, 0, TARGET_W, TARGET_H);
        const maxW = TARGET_W - PADDING * 2;
        const maxH = TARGET_H - PADDING * 2;
        const posterScale = Math.min(maxW / canvas.width, maxH / canvas.height);
        const drawW = Math.round(canvas.width * posterScale);
        const drawH = Math.round(canvas.height * posterScale);
        const offsetX = Math.round((TARGET_W - drawW) / 2);
        const offsetY = Math.round((TARGET_H - drawH) / 2);
        ctx.drawImage(canvas, offsetX, offsetY, drawW, drawH);
        finalCanvas = outputCanvas;
      }

      // Convert canvas to blob with DPI metadata for poster
      const rawBlob = await new Promise(resolve => finalCanvas.toBlob(resolve, 'image/png'));
      const blob = format === 'poster' ? await embedPngDpi(rawBlob, 300) : rawBlob;
      const fileName = `${displayAthlete.athlete_name.replace(/\s+/g, '-')}-spotlight-${format}.png`;
      const file = new File([blob], fileName, { type: 'image/png' });

      // Check if Web Share API is available and can share files
      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({
            files: [file],
            title: `${displayAthlete.athlete_name} Spotlight`,
            text: `Check out ${displayAthlete.athlete_name}'s spotlight on LegacyStat!`
          });
          // Close modal after successful share
          setIsDownloadModalOpen(false);
        } catch (shareError) {
          // User cancelled or share failed
          if (shareError.name !== 'AbortError') {
            // If share fails for other reasons, fall back to download
            downloadFallback(finalCanvas, fileName);
          }
          // Keep modal open if user cancelled
        }
      } else {
        // Web Share not available - fall back to download
        downloadFallback(finalCanvas, fileName);
      }
    } catch (error) {
      console.error('Direct share failed:', error);
      alert('Share failed. Please try again.');
    } finally {
      if (directShareRestoreImages) directShareRestoreImages();
      setIsDownloading(false);
    }
  };

  const handleDownload = async (format = 'poster') => {
    if (athlete?.id) {
      await trackAnalytics(athlete.id, 'download');
    }

    // On iOS devices, automatically trigger Share Sheet instead of showing download modal
    if (isIOS() && navigator.share && navigator.canShare) {
      // Set format for rendering
      setDownloadFormat(format);
      setIsShareModalOpen(false);
      setIsDownloadModalOpen(true);

      // Wait for modal to render and useEffect to apply word-boundary truncation
      setTimeout(async () => {
        await handleDirectShare(format);
      }, 200);
    } else {
      // Desktop/Android: Show download modal as normal
      setDownloadFormat(format);
      setIsShareModalOpen(false);
      setIsDownloadModalOpen(true);
    }
  };

  // Actually download the spotlight card as image
  // CRC32 for PNG chunk checksums
  const crc32 = (data) => {
    let crc = 0xFFFFFFFF;
    for (let i = 0; i < data.length; i++) {
      crc ^= data[i];
      for (let j = 0; j < 8; j++) {
        crc = (crc >>> 1) ^ (crc & 1 ? 0xEDB88320 : 0);
      }
    }
    return (crc ^ 0xFFFFFFFF) >>> 0;
  };

  // Embed 300 DPI metadata (pHYs chunk) into PNG blob
  const embedPngDpi = (blob, dpi = 300) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        const buffer = reader.result;
        // pHYs chunk: insert after PNG signature (8 bytes) + IHDR chunk (25 bytes) = offset 33
        const insertOffset = 33;
        const ppm = Math.round(dpi / 0.0254); // pixels per meter
        // Build pHYs chunk: 4(length) + 4(type) + 9(data) + 4(CRC) = 21 bytes
        const phys = new Uint8Array(21);
        const physView = new DataView(phys.buffer);
        physView.setUint32(0, 9); // data length
        phys[4] = 0x70; phys[5] = 0x48; phys[6] = 0x59; phys[7] = 0x73; // "pHYs"
        physView.setUint32(8, ppm);  // X pixels per unit
        physView.setUint32(12, ppm); // Y pixels per unit
        phys[16] = 1; // unit = meter
        physView.setUint32(17, crc32(phys.slice(4, 17)));
        // Combine: before + pHYs + after
        const before = new Uint8Array(buffer, 0, insertOffset);
        const after = new Uint8Array(buffer, insertOffset);
        const result = new Uint8Array(before.length + phys.length + after.length);
        result.set(before, 0);
        result.set(phys, insertOffset);
        result.set(after, insertOffset + phys.length);
        resolve(new Blob([result], { type: 'image/png' }));
      };
      reader.readAsArrayBuffer(blob);
    });
  };

  // Pre-crop an image to match its displayed object-fit:cover crop
  const preCropImage = (imgEl) => {
    return new Promise((resolve) => {
      const containerW = imgEl.clientWidth;
      const containerH = imgEl.clientHeight;
      if (!containerW || !containerH) { resolve(null); return; }
      const tempImg = new Image();
      tempImg.crossOrigin = 'anonymous';
      tempImg.onload = () => {
        const natW = tempImg.naturalWidth;
        const natH = tempImg.naturalHeight;
        // Calculate cover crop (same logic as object-fit: cover)
        const scale = Math.max(containerW / natW, containerH / natH);
        const cropW = containerW / scale;
        const cropH = containerH / scale;
        // Determine object-position from computed style
        const computed = window.getComputedStyle(imgEl);
        const objPos = computed.objectPosition || 'center center';
        const parts = objPos.split(/\s+/);
        const posX = parts[0] || '50%';
        const posY = parts[1] || '50%';
        // Calculate source x based on horizontal position
        let sx;
        if (posX === 'center' || posX === '50%') sx = (natW - cropW) / 2;
        else if (posX === 'left' || posX === '0%') sx = 0;
        else if (posX === 'right' || posX === '100%') sx = natW - cropW;
        else sx = (natW - cropW) * (parseFloat(posX) / 100);
        // Calculate source y based on vertical position
        let sy;
        if (posY === 'center' || posY === '50%') sy = (natH - cropH) / 2;
        else if (posY === 'top' || posY === '0%') sy = 0;
        else if (posY === 'bottom' || posY === '100%') sy = natH - cropH;
        else sy = (natH - cropH) * (parseFloat(posY) / 100);
        // Draw cropped portion at high resolution
        const outW = containerW * 4;
        const outH = containerH * 4;
        const cropCanvas = document.createElement('canvas');
        cropCanvas.width = outW;
        cropCanvas.height = outH;
        const ctx = cropCanvas.getContext('2d');
        ctx.drawImage(tempImg, sx, sy, cropW, cropH, 0, 0, outW, outH);
        resolve(cropCanvas.toDataURL('image/png'));
      };
      tempImg.onerror = () => resolve(null);
      tempImg.src = imgEl.src;
    });
  };

  // Pre-crop all object-fit:cover images in a container, returns restore function
  const preCropAllImages = async (container) => {
    const allImgs = container.querySelectorAll('img');
    const originals = [];
    for (const img of allImgs) {
      const computed = window.getComputedStyle(img);
      if (computed.objectFit === 'cover') {
        const originalSrc = img.src;
        const originalFit = img.style.objectFit;
        const originalPos = img.style.objectPosition;
        const croppedDataUrl = await preCropImage(img);
        if (croppedDataUrl) {
          img.src = croppedDataUrl;
          img.style.objectFit = 'fill';
          img.style.objectPosition = 'center center';
          originals.push({ img, originalSrc, originalFit, originalPos });
        }
      }
    }
    // Wait for browser to apply new sources
    if (originals.length > 0) {
      await new Promise(r => setTimeout(r, 50));
    }
    // Return restore function
    return () => {
      for (const { img, originalSrc, originalFit, originalPos } of originals) {
        img.src = originalSrc;
        img.style.objectFit = originalFit;
        img.style.objectPosition = originalPos;
      }
    };
  };

  const handleDownloadCard = async () => {
    if (!downloadCardRef.current) return;

    setIsDownloading(true);

    let restoreImages = null;

    try {
      // Text is already truncated at word boundaries by the useEffect
      let renderScale = 2;
      if (downloadFormat === 'poster') {
        renderScale = 4;
      }

      // Pre-crop all object-fit:cover images to match displayed crop
      restoreImages = await preCropAllImages(downloadCardRef.current);

      const canvas = await html2canvas(downloadCardRef.current, {
        backgroundColor: '#000000',
        scale: renderScale,
        useCORS: true, // Allow cross-origin images
        allowTaint: true,
        logging: false
      });

      let finalCanvas = canvas;

      // For poster format, place rendered canvas inside a 3600×4500 canvas with padding (4:5 ratio)
      if (downloadFormat === 'poster') {
        const TARGET_W = 3600;
        const TARGET_H = 4500;
        const PADDING = 120; // ~0.4 inch safe zone at 300 PPI

        const outputCanvas = document.createElement('canvas');
        outputCanvas.width = TARGET_W;
        outputCanvas.height = TARGET_H;
        const ctx = outputCanvas.getContext('2d');

        // Fill background
        ctx.fillStyle = '#000000';
        ctx.fillRect(0, 0, TARGET_W, TARGET_H);

        // Scale the rendered poster to fit within padded area, keeping aspect ratio
        const maxW = TARGET_W - PADDING * 2;
        const maxH = TARGET_H - PADDING * 2;
        const scale = Math.min(maxW / canvas.width, maxH / canvas.height);
        const drawW = Math.round(canvas.width * scale);
        const drawH = Math.round(canvas.height * scale);
        const offsetX = Math.round((TARGET_W - drawW) / 2);
        const offsetY = Math.round((TARGET_H - drawH) / 2);

        ctx.drawImage(canvas, offsetX, offsetY, drawW, drawH);
        finalCanvas = outputCanvas;

        console.log(`[Spotlight Download] Format: poster | Output: ${TARGET_W}×${TARGET_H}px | Poster: ${drawW}×${drawH}px | DPI: 300`);
      } else {
        const dpi = 72;
        console.log(`[Spotlight Download] Format: ${downloadFormat} | Size: ${canvas.width}×${canvas.height}px | DPI: ${dpi} | Scale: ${renderScale}`);
      }

      // Convert to blob, embed DPI metadata for poster, then download
      const rawBlob = await new Promise(resolve => finalCanvas.toBlob(resolve, 'image/png'));
      const finalBlob = downloadFormat === 'poster' ? await embedPngDpi(rawBlob, 300) : rawBlob;
      const url = URL.createObjectURL(finalBlob);
      const link = document.createElement('a');
      link.download = `${displayAthlete.athlete_name.replace(/\s+/g, '-')}-spotlight-${downloadFormat}.png`;
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Download failed. Please try again.');
    } finally {
      // Restore all pre-cropped images back to originals
      if (restoreImages) restoreImages();
      setIsDownloading(false);
    }
  };

  // Save to Phone - Uses Web Share API on mobile, falls back to download
  const handleSaveToPhone = async () => {
    if (athlete?.id) {
      await trackAnalytics(athlete.id, 'save_to_phone');
    }

    // Close share modal and open download modal with poster format
    setDownloadFormat('poster');
    setIsShareModalOpen(false);
    setIsDownloadModalOpen(true);

    // Wait for modal to render and useEffect to apply word-boundary truncation
    setTimeout(async () => {
      if (!downloadCardRef.current) return;

      setIsDownloading(true);

      let mobileRestoreImages = null;

      try {
        // Text is already truncated at word boundaries by the useEffect
        // Use same scale as handleDownloadCard for consistent rendering
        const mobileScale = 4;

        // Pre-crop all object-fit:cover images to match displayed crop
        mobileRestoreImages = await preCropAllImages(downloadCardRef.current);

        const canvas = await html2canvas(downloadCardRef.current, {
          backgroundColor: '#000000',
          scale: mobileScale,
          useCORS: true,
          allowTaint: true,
          logging: false
        });

        // Recompose onto print-ready 3600×4500 canvas (same as desktop poster)
        const TARGET_W = 3600;
        const TARGET_H = 4500;
        const PADDING = 120;
        const outputCanvas = document.createElement('canvas');
        outputCanvas.width = TARGET_W;
        outputCanvas.height = TARGET_H;
        const ctx = outputCanvas.getContext('2d');
        ctx.fillStyle = '#000000';
        ctx.fillRect(0, 0, TARGET_W, TARGET_H);
        const maxW = TARGET_W - PADDING * 2;
        const maxH = TARGET_H - PADDING * 2;
        const posterScale = Math.min(maxW / canvas.width, maxH / canvas.height);
        const drawW = Math.round(canvas.width * posterScale);
        const drawH = Math.round(canvas.height * posterScale);
        const offsetX = Math.round((TARGET_W - drawW) / 2);
        const offsetY = Math.round((TARGET_H - drawH) / 2);
        ctx.drawImage(canvas, offsetX, offsetY, drawW, drawH);

        // Convert to blob with 300 DPI metadata for print
        const rawBlob = await new Promise(resolve => outputCanvas.toBlob(resolve, 'image/png'));
        const blob = await embedPngDpi(rawBlob, 300);
        const fileName = `${displayAthlete.athlete_name.replace(/\s+/g, '-')}-spotlight.png`;
        const file = new File([blob], fileName, { type: 'image/png' });

        // Check if Web Share API is available and can share files
        if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
          try {
            await navigator.share({
              files: [file],
              title: `${displayAthlete.athlete_name} Spotlight`,
              text: `Check out ${displayAthlete.athlete_name}'s spotlight on LegacyStat!`
            });
          } catch (shareError) {
            // User cancelled or share failed - fall back to download
            if (shareError.name !== 'AbortError') {
              downloadFallback(outputCanvas, fileName);
            }
          }
        } else {
          // Web Share not available - fall back to download
          downloadFallback(outputCanvas, fileName);
        }
      } catch (error) {
        console.error('Save to phone failed:', error);
        alert('Save failed. Please try again.');
      } finally {
        // Restore all pre-cropped images back to originals
        if (mobileRestoreImages) mobileRestoreImages();
        setIsDownloading(false);
      }
    }, 200);
  };

  // Fallback download function
  const downloadFallback = (canvas, fileName) => {
    const link = document.createElement('a');
    link.download = fileName;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  // Fallback athlete data if none loaded
  const displayAthlete = athlete || {
    athlete_name: 'Loading...',
    primary_sport: '',
    city: '',
    state: '',
    hero_image: '/assets/images/athlete-placeholder.png',
    moment_headline: 'The Moment',
    regional_label: '',
    moment_description: '',
    official_caption: '',
    time_label: ''
  };

  const spotlightNumber = String(
    location.state?.orderPosition ?? displayAthlete.featured_athletes?.[0]?.order_position ?? 1
  ).padStart(2, '0');

  return (
    <div className="athlete-details-wrapper">
      {/* Mobile Header - Only visible on mobile */}
      <div className="athlete-details-mobile-header">
        <button className="athlete-details-back-btn" onClick={() => setActiveScreen('featured-athletes')}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <h1 className="athlete-details-mobile-title">Featured Athlete</h1>
      </div>

      {/* Desktop Header */}
      <Header activeScreen="featured-athletes" setActiveScreen={setActiveScreen} spotlightMode={true} />

      {/* Desktop Back Button */}
      <button
        className="athlete-details-desktop-back-btn"
        onClick={() => setActiveScreen('featured-athletes')}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <span>Back</span>
      </button>

      <div className="athlete-details-container">
        <div className="athlete-details-content">
          {/* Top Light Glow Effect */}
          <div className="athlete-details-top-light-glow">
            <img src="/assets/images/gold-icon.png" alt="" />
          </div>

          {/* Left Side - Athlete Card */}
          <div className="athlete-details-card-wrapper">
            <div style={{ transform: 'scale(1.25)', transformOrigin: 'top center' }}>
              <AthleteCard
                image={displayAthlete.hero_image}
                name={displayAthlete.athlete_name}
                sport={displayAthlete.primary_sport}
                location={`${displayAthlete.city}, ${displayAthlete.state}`}
                timeLabel={displayAthlete.time_label}
                athleteNumber={location.state?.orderPosition ?? displayAthlete.featured_athletes?.[0]?.order_position}
                spotlightLabel={displayAthlete.regional_label}
              />
            </div>
          </div>

          {/* Right Side - Spotlight Content */}
          <div className="athlete-details-spotlight-wrapper">
            {/* Spotlight Header */}
            {/* COMMENTED OUT - Proof Lives here section
            <div className="athlete-details-spotlight-header">
              <div className="athlete-details-spotlight-header-content">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <img
                      src="/assets/images/star-icon.png"
                      alt="Star"
                      className="athlete-details-star-icon"
                    />
                    <h3 className="athlete-details-spotlight-title">Proof Lives here.</h3>
                  </div>
                  <svg
                    className={`athlete-details-dropdown-icon ${isDescriptionExpanded ? 'expanded' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                    style={{ cursor: 'pointer' }}
                  >
                    <path d="M19 9L12 16L5 9" stroke="#999999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <p className={`athlete-details-spotlight-desc ${!isDescriptionExpanded ? 'truncated' : ''}`}>
                  {isDescriptionExpanded ? 'Where youth sports gets celebrated.' : 'LegacyStat Spotlight highlights athletes who showed up, put in the work, and earned their moment'}
                </p>
              </div>
            </div>
            */}

            {/* Legacy Spotlight Section */}
            <div className="athlete-details-legacy-section">
              <div className="athlete-details-legacy-header">
                <img
                  src="/assets/images/spotlight-icon.png"
                  alt="Spotlight"
                  className="athlete-details-legacy-icon"
                />
                <h4 className="athlete-details-legacy-title">Spotlight Athlete #{spotlightNumber}</h4>
              </div>
              {(displayAthlete.official_caption || 'This athlete earned their spotlight through consistency, leadership, and showing up when it mattered.').split('\n').filter(p => p.trim()).map((paragraph, idx) => (
                <p key={idx} className="athlete-details-legacy-story">{paragraph}</p>
              ))}
            </div>

            {/* Gallery - Video from video_uid + Images from moment_media */}
            {(displayAthlete.video_uid || (displayAthlete.moment_media && displayAthlete.moment_media.length > 0)) && (
              <div className="athlete-details-gallery">
                {/* Show video from video_uid first (if exists) */}
                {displayAthlete.video_uid && (
                  <div key="spotlight-video" className="athlete-details-gallery-item">
                    <div
                      className="athlete-details-video-wrapper"
                      style={{
                        width: '100%',
                        height: '100%',
                        position: 'relative'
                      }}
                    >
                      <iframe
                        src={`https://iframe.cloudflarestream.com/${displayAthlete.video_uid}`}
                        title="Spotlight video"
                        allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
                        allowFullScreen
                        className="athlete-details-gallery-video"
                        style={{ width: '100%', height: '100%', borderRadius: '8px', border: 'none' }}
                      />
                      {/* Overlay to capture clicks on mobile */}
                      <div
                        className="video-click-overlay"
                        onClick={() => setVideoPreview(`https://iframe.cloudflarestream.com/${displayAthlete.video_uid}`)}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: 'rgba(0, 0, 0, 0.3)',
                          cursor: 'pointer',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <div style={{
                          color: 'white',
                          fontSize: '14px',
                          fontWeight: '600',
                          padding: '8px 16px',
                          background: 'rgba(0, 0, 0, 0.6)',
                          borderRadius: '4px'
                        }}>
                          Tap to view fullscreen
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Show images from moment_media */}
                {displayAthlete.moment_media && displayAthlete.moment_media.length > 0 && (
                  <>
                    {[...displayAthlete.moment_media]
                      .map(media => {
                        // Handle stringified JSON, plain URLs, and objects
                        // moment_media now only contains images (no videos)
                        if (typeof media === 'string') {
                          if (media.startsWith('{')) {
                            try {
                              const parsed = JSON.parse(media);
                              return { url: parsed.url, type: 'image' };
                            } catch (e) {
                              return { url: media, type: 'image' };
                            }
                          }
                          return { url: media, type: 'image' };
                        }
                        return { url: media.url, type: 'image' };
                      })
                      .map((media, index) => (
                        <div key={`image-${index}`} className="athlete-details-gallery-item">
                          <img
                            src={media.url}
                            alt={`Gallery ${index + 1}`}
                            className="athlete-details-gallery-image"
                            onClick={() => setImagePreview(media.url)}
                            style={{ cursor: 'pointer' }}
                            onError={(e) => {
                              console.error('Image failed to load:', media.url);
                              e.target.src = '/assets/images/placeholder-1.png';
                            }}
                          />
                        </div>
                      ))}
                  </>
                )}
              </div>
            )}


            {/* Share Spotlight Button */}
            <button className="athlete-details-share-btn" onClick={handleShare}>
              <img
                src="/assets/images/share-icon.png"
                alt="Share"
                className="athlete-details-share-icon"
              />
              Share Spotlight
            </button>
          </div>
        </div>
      </div>

      {/* Download Card Modal */}
      {isDownloadModalOpen && (
        <>
          <div className="athlete-details-modal-overlay" onClick={() => setIsDownloadModalOpen(false)}></div>
          <div className="athlete-details-download-modal">
            {/* Close Button */}
            <button className="athlete-details-download-modal-close" onClick={() => setIsDownloadModalOpen(false)}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6L18 18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            {/* POSTER FORMAT (Default) */}
            {downloadFormat === 'poster' && (
              <div className="athlete-details-download-card download-card-poster" ref={downloadCardRef}>

                {/* ── Hero Image Section ── */}
                <div className="poster-hero-section">
                  <img
                    src={displayAthlete.hero_image}
                    alt={displayAthlete.athlete_name}
                    className="poster-hero-img"
                  />

                  {/* LS logo top-left */}
                  <img src="/assets/images/logo1.png" alt="LS" className="poster-ls-corner-logo" />

                  {/* Dark gradient overlay */}
                  <div className="poster-gradient-overlay" />

                  {/* Text overlaid on image */}
                  <div className="poster-overlay-content">

                    {/* Location pill */}
                    <div className="poster-location-pill">
                      <svg width="11" height="11" viewBox="0 0 16 16" fill="none">
                        <path d="M8 8.66667C9.10457 8.66667 10 7.77124 10 6.66667C10 5.5621 9.10457 4.66667 8 4.66667C6.89543 4.66667 6 5.5621 6 6.66667C6 7.77124 6.89543 8.66667 8 8.66667Z" stroke="#D8A134" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M8 14.6667C10.6667 12 13.3333 9.61218 13.3333 6.66667C13.3333 3.72115 10.9455 1.33333 8 1.33333C5.05448 1.33333 2.66667 3.72115 2.66667 6.66667C2.66667 9.61218 5.33333 12 8 14.6667Z" stroke="#D8A134" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>{displayAthlete.city}, {displayAthlete.state}</span>
                    </div>

                    {/* Athlete name */}
                    <h2 className="poster-athlete-name">{(displayAthlete.athlete_name || '').toUpperCase()}</h2>

                    {/* Spotlight badge */}
                    <p className="poster-spotlight-badge">SPOTLIGHT ATHLETE #{spotlightNumber}</p>

                    {/* Gradient divider below badge */}
                    <div style={{
                      alignSelf: 'stretch', height: '1px', margin: '4px 0',
                      background: 'linear-gradient(90deg, transparent 0%, rgba(216,161,52,0.1) 20%, rgba(216,161,52,0.4) 45%, rgba(216,161,52,0.5) 50%, rgba(216,161,52,0.4) 55%, rgba(216,161,52,0.1) 80%, transparent 100%)'
                    }} />

                    {/* Sport */}
                    <p className="poster-sport">{(displayAthlete.primary_sport || '').toUpperCase()}</p>

                    {/* Time label only — no THE MOMENT */}
                    {displayAthlete.time_label && (
                      <p className="poster-org-season">{displayAthlete.time_label.toUpperCase()}</p>
                    )}

                    {/* Regional label */}
                    {displayAthlete.regional_label && (
                      <p className="poster-regional-label">{displayAthlete.regional_label.toUpperCase()}</p>
                    )}

                    {/* Gold divider above QR row */}
                    <div className="poster-qr-divider" />

                    {/* QR + Scan + Brand row — overlaid on image */}
                    <div className="poster-qr-row">

                      <div style={{ flexShrink: 0 }}>
                        <QRCodeSVG
                          value={athleteId ? getSpotlightUrl(athleteId) : window.location.href}
                          size={56}
                          bgColor="#ffffff"
                          fgColor="#000000"
                          level="H"
                          imageSettings={{
                            src: "/assets/images/legacy logo plain black.png",
                            height: 13,
                            width: 13,
                            excavate: true,
                          }}
                        />
                      </div>

                      <div className="poster-scan-section">
                        <div className="poster-scan-text">
                          <span>SCAN TO VIEW</span>
                          <span>THIS ATHLETE'S</span>
                          <span className="poster-scan-highlight">SPOTLIGHT</span>
                        </div>
                      </div>

                      <div className="poster-brand-section">
                        <div className="poster-brand-row">
                          <img src="/assets/images/logo1.png" alt="LS" className="poster-brand-icon" />
                          <span className="poster-brand-name">LEGACY<span className="poster-brand-stat">STAT</span></span>
                        </div>
                        <p className="poster-tagline">PROOF <span className="poster-tagline-lives">LIVES</span> HERE.</p>
                        <p className="poster-website">LEGACYSTAT.COM</p>
                      </div>

                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* VERTICAL FORMAT (Stories/Reels - 9:16) */}
            {downloadFormat === 'vertical' && (
              <div className="athlete-details-download-card download-card-vertical" ref={downloadCardRef}>

                {/* ── Full-bleed hero + all content overlaid ── */}
                <div className="vert-hero-section">
                  <img src={displayAthlete.hero_image} alt={displayAthlete.athlete_name} className="vert-hero-img" />
                  <img src="/assets/images/logo1.png" alt="LS" className="vert-ls-logo" />
                  <div className="vert-gradient-overlay" />

                  <div className="vert-overlay-content">

                    {/* Location + Name */}
                    <div className="vert-location-pill">
                      <svg width="11" height="11" viewBox="0 0 16 16" fill="none">
                        <path d="M8 8.66667C9.10457 8.66667 10 7.77124 10 6.66667C10 5.5621 9.10457 4.66667 8 4.66667C6.89543 4.66667 6 5.5621 6 6.66667C6 7.77124 6.89543 8.66667 8 8.66667Z" stroke="#D8A134" strokeWidth="1.2"/>
                        <path d="M8 14.6667C10.6667 12 13.3333 9.61218 13.3333 6.66667C13.3333 3.72115 10.9455 1.33333 8 1.33333C5.05448 1.33333 2.66667 3.72115 2.66667 6.66667C2.66667 9.61218 5.33333 12 8 14.6667Z" stroke="#D8A134" strokeWidth="1.2"/>
                      </svg>
                      <span>{displayAthlete.city}, {displayAthlete.state}</span>
                    </div>
                    <h2 className="vert-athlete-name">{(displayAthlete.athlete_name || '').toUpperCase()}</h2>

                    {/* Info text */}
                    <p className="vert-spotlight-badge">SPOTLIGHT ATHLETE #{spotlightNumber}</p>

                    {/* Gradient divider below badge */}
                    <div style={{
                      alignSelf: 'stretch', height: '1px', margin: '4px 0',
                      background: 'linear-gradient(90deg, transparent 0%, rgba(216,161,52,0.1) 20%, rgba(216,161,52,0.4) 45%, rgba(216,161,52,0.5) 50%, rgba(216,161,52,0.4) 55%, rgba(216,161,52,0.1) 80%, transparent 100%)'
                    }} />

                    <p className="vert-sport">{(displayAthlete.primary_sport || '').toUpperCase()}</p>

                    {/* Time label only — no THE MOMENT */}
                    {displayAthlete.time_label && (
                      <p className="vert-org-season">{displayAthlete.time_label.toUpperCase()}</p>
                    )}
                    {displayAthlete.regional_label && (
                      <p className="vert-regional">{displayAthlete.regional_label.toUpperCase()}</p>
                    )}

                    {/* Gradient divider */}
                    <div style={{
                      alignSelf: 'stretch',
                      height: '2px',
                      margin: '0 0 0',
                      background: 'linear-gradient(90deg, transparent 0%, rgba(216,161,52,0.1) 20%, rgba(216,161,52,0.4) 45%, rgba(216,161,52,0.5) 50%, rgba(216,161,52,0.4) 55%, rgba(216,161,52,0.1) 80%, transparent 100%)',
                      flexShrink: 0
                    }} />

                    {/* Brand — centered, no QR */}
                    <div className="vert-brand-section">
                      <div className="vert-brand-row">
                        <img src="/assets/images/logo1.png" alt="LS" className="vert-brand-icon" />
                        <span className="vert-brand-name">LEGACY<span className="vert-brand-stat">STAT</span><span className="vert-brand-dot">.</span></span>
                      </div>
                      <p className="vert-tagline">PROOF <span className="vert-tagline-lives">LIVES</span> HERE.</p>
                    </div>

                  </div>
                </div>

              </div>
            )}

            {/* SQUARE FORMAT (Feed - 1:1) */}
            {downloadFormat === 'square' && (
              <div className="athlete-details-download-card download-card-square" ref={downloadCardRef}>

                {/* ── Left: Hero Image ── */}
                <div className="sq-hero-col">
                  <img src="/assets/images/logo1.png" alt="LS" className="sq-ls-logo" />
                  <img
                    src={displayAthlete.hero_image}
                    alt={displayAthlete.athlete_name}
                    className="sq-hero-img"
                  />
                  {/* Right-edge fade to blend with dark panel */}
                  <div className="sq-hero-fade" />
                </div>

                {/* ── Right: Info Panel ── */}
                <div className="sq-info-col">

                  {/* Location pill */}
                  <div className="sq-location-pill">
                    <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
                      <path d="M8 8.66667C9.10457 8.66667 10 7.77124 10 6.66667C10 5.5621 9.10457 4.66667 8 4.66667C6.89543 4.66667 6 5.5621 6 6.66667C6 7.77124 6.89543 8.66667 8 8.66667Z" stroke="#D8A134" strokeWidth="1.2"/>
                      <path d="M8 14.6667C10.6667 12 13.3333 9.61218 13.3333 6.66667C13.3333 3.72115 10.9455 1.33333 8 1.33333C5.05448 1.33333 2.66667 3.72115 2.66667 6.66667C2.66667 9.61218 5.33333 12 8 14.6667Z" stroke="#D8A134" strokeWidth="1.2"/>
                    </svg>
                    <span>{displayAthlete.city}, {displayAthlete.state}</span>
                  </div>

                  {/* Athlete name */}
                  <h2 className="sq-athlete-name">{(displayAthlete.athlete_name || '').toUpperCase()}</h2>

                  {/* Gold gradient divider */}
                  <div className="sq-name-divider" />

                  {/* Spotlight # */}
                  <p className="sq-spotlight-badge">SPOTLIGHT ATHLETE #{spotlightNumber}</p>

                  {/* Divider below spotlight badge */}
                  <div className="sq-badge-divider" />

                  {/* Sport */}
                  <p className="sq-sport">{(displayAthlete.primary_sport || '').toUpperCase()}</p>

                  {/* Time label only — no "THE MOMENT" default */}
                  {displayAthlete.time_label && (
                    <p className="sq-org-season">{displayAthlete.time_label.toUpperCase()}</p>
                  )}

                  {/* Regional label */}
                  {displayAthlete.regional_label && (
                    <p className="sq-regional">{displayAthlete.regional_label.toUpperCase()}</p>
                  )}

                  {/* Description — first paragraph of official_caption (same as detail view) */}
                  {displayAthlete.official_caption && (
                    <div className="sq-desc-block">
                      <p className="sq-desc-heading">Bio</p>
                      <p className="sq-moment-desc">
                        {displayAthlete.official_caption.split('\n').filter(p => p.trim())[0] || displayAthlete.official_caption}
                      </p>
                    </div>
                  )}

                  <div className="sq-bottom-spacer" />

                  {/* Gradient divider */}
                  <div style={{
                    alignSelf: 'stretch',
                    height: '2px',
                    marginBottom: '8px',
                    background: 'linear-gradient(90deg, transparent 0%, rgba(216,161,52,0.1) 20%, rgba(216,161,52,0.4) 45%, rgba(216,161,52,0.5) 50%, rgba(216,161,52,0.4) 55%, rgba(216,161,52,0.1) 80%, transparent 100%)',
                  }} />

                  {/* Brand — centered, no QR */}
                  <div className="sq-brand">
                    <div className="sq-brand-row">
                      <img src="/assets/images/logo1.png" alt="LS" className="sq-brand-icon" />
                      <span className="sq-brand-name">LEGACY<span className="sq-brand-stat">STAT</span><span className="sq-brand-dot">.</span></span>
                    </div>
                    <p className="sq-tagline">PROOF <span className="sq-tagline-lives">LIVES</span> HERE.</p>
                  </div>

                </div>
              </div>
            )}

            {/* Download Button */}
            <button
              className="athlete-details-download-btn"
              onClick={handleDownloadCard}
              disabled={isDownloading}
            >
              <img src="/assets/images/download-icon.png" alt="Download" style={{ width: '20px', height: '20px' }} />
              {isDownloading ? 'Downloading...' : 'Download'}
            </button>
          </div>
        </>
      )}

      {/* Share Modal */}
      {isShareModalOpen && (
        <>
          <div className="athlete-details-modal-overlay" onClick={() => setIsShareModalOpen(false)}></div>
          <div className="athlete-details-share-modal">
            <div className="athlete-details-modal-header">
              <img src="/assets/images/logo1.png" alt="LegacyStat" style={{ width: '31px', height: '40px' }} />
              <h3 className="athlete-details-modal-title">Share Spotlight</h3>
              <button className="athlete-details-modal-close" onClick={() => setIsShareModalOpen(false)}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6L18 18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>

            <div className="athlete-details-modal-content">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0px' }}>
                <p className="athlete-details-modal-subtitle" style={{ margin: 0 }}>Use this option to share this athlete's Spotlight.</p>
                <button className="athlete-details-social-icon" onClick={handleCopyLink} style={{ width: '48px', height: '48px', padding: '8px' }}>
                  <img src="/assets/images/link-icon1.png" alt="Link" style={{ width: '100%', height: '100%' }} />
                </button>
              </div>

              {/* Download Formats Section */}
              <h4 className="athlete-details-download-section-title">Download Formats</h4>

              <button className="athlete-details-modal-item" onClick={() => handleDownload('poster')}>
                <div className="athlete-details-modal-item-icon">
                  <img src="/assets/images/format-icon.png" alt="Download" />
                </div>
                <div className="athlete-details-modal-item-text">
                  <div className="athlete-details-modal-item-title">Download Poster Spotlight</div>
                </div>
                <svg className="athlete-details-modal-item-arrow" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M9 18l6-6-6-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>

              <button className="athlete-details-modal-item" onClick={() => handleDownload('vertical')}>
                <div className="athlete-details-modal-item-icon">
                  <img src="/assets/images/format-icon.png" alt="Download" />
                </div>
                <div className="athlete-details-modal-item-text">
                  <div className="athlete-details-modal-item-title">Download Vertical Spotlight</div>
                  <div className="athlete-details-modal-item-desc">(Stories/Reels)</div>
                </div>
                <svg className="athlete-details-modal-item-arrow" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M9 18l6-6-6-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>

              <button className="athlete-details-modal-item" onClick={() => handleDownload('square')}>
                <div className="athlete-details-modal-item-icon">
                  <img src="/assets/images/format-icon.png" alt="Download" />
                </div>
                <div className="athlete-details-modal-item-text">
                  <div className="athlete-details-modal-item-title">Download Square Spotlight</div>
                  <div className="athlete-details-modal-item-desc">(Feed)</div>
                </div>
                <svg className="athlete-details-modal-item-arrow" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M9 18l6-6-6-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>

            </div>
          </div>
        </>
      )}

      {/* Image Preview Modal */}
      {imagePreview && (
        <>
          <div className="athlete-details-modal-overlay" onClick={() => setImagePreview(null)}></div>
          <div className="athlete-details-image-preview-modal">
            <button className="athlete-details-image-preview-close" onClick={() => setImagePreview(null)}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6L18 18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <img src={imagePreview} alt="Preview" className="athlete-details-image-preview" />
          </div>
        </>
      )}

      {/* Video Preview Modal */}
      {videoPreview && (
        <>
          <div className="athlete-details-modal-overlay" onClick={() => setVideoPreview(null)}></div>
          <div className="athlete-details-video-preview-modal">
            <button className="athlete-details-video-preview-close" onClick={() => setVideoPreview(null)}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6L18 18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <div className="athlete-details-video-container">
              {isYouTubeUrl(videoPreview) ? (
                <iframe
                  src={`https://www.youtube.com/embed/${getYouTubeVideoId(videoPreview)}?autoplay=1&rel=0&modestbranding=1&controls=1`}
                  title="YouTube video player"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{ width: '100%', height: '100%', borderRadius: '12px', border: 'none' }}
                />
              ) : isCloudflareStreamUrl(videoPreview) ? (
                <iframe
                  src={`${videoPreview}?autoplay=true&controls=true`}
                  title="Cloudflare Stream video"
                  allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
                  allowFullScreen
                  style={{ width: '100%', height: '100%', borderRadius: '12px', border: 'none' }}
                />
              ) : (
                <video
                  src={videoPreview}
                  controls
                  autoPlay
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    borderRadius: '12px'
                  }}
                />
              )}
            </div>
          </div>
        </>
      )}

      <MobileBottomNav activeTab="athlete-details" setActiveTab={setActiveScreen} />
    </div>
  );
};

export default AthleteDetails;
